import React from 'react';

import { DownloadRounded } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';

import Button from '../../../../components/Button';
import DataGrid, { DataGridProps } from '../../../../components/DataGrid';
import { useLazyGetExamAttemptFileUrlQuery } from '../../../../services/exams';
import useDownload from '../../../../utils/react/hooks/useDownload';
import useError from '../../../../utils/react/hooks/useError';
import { DueDateText } from '../../index.styles';

interface AttemptsListProps extends Omit<DataGridProps, 'columns'> {
  isMarked: boolean,
}

const AttemptsList = ({ isMarked, ...props }: AttemptsListProps) => {
  const [getDownloadUrl, {
    data: downloadUrlData,
    isError: isDownloadUrlError,
    error: downloadUrlError,
  }] = useLazyGetExamAttemptFileUrlQuery();

  useDownload(downloadUrlData?.data);

  useError(isDownloadUrlError, downloadUrlError);

  return (
    <DataGrid
      initialState={{
        sorting: {
          sortModel: isMarked ? [
            { field: 'markedDatetime', sort: 'desc' },
          ] : [
            { field: 'dueDatetime', sort: 'asc' },
          ],
        },
      }}
      columns={[
        {
          field: 'options',
          headerName: 'Options',
          width: 120,
          renderCell: ({ row }: any) => (
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                color="info"
                disabled={!row.unmarkedExamFileName}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  await getDownloadUrl({
                    subject: row.subject,
                    examId: row.examId,
                    studentId: row.student,
                    attemptNum: row.attemptNumber,
                  });
                }}
              >
                <DownloadRounded />
              </IconButton>
              <Button
                size="small"
                variant="text"
                href={`${row.subject}/${row.examId}/${row.student}/${row.attemptNumber}`}
              >
                {isMarked ? 'Remark' : 'Mark'}
              </Button>
            </Stack>
          ),
        },
        ...(isMarked ? [{
          field: 'markedDatetime',
          headerName: 'Date',
          width: 80,
          renderCell: (e: any) => dayjs(e.value).format('D-MMM'),
        }] : [{
          field: 'dueDatetime',
          headerName: 'Due',
          width: 120,
          renderCell: ({ row } : any) => (
            <DueDateText $overdue={row?.dueDatetime && row.dueDatetime < dayjs().valueOf()}>
              {row.dueDatetime ? _.upperFirst(dayjs(row.dueDatetime).fromNow()) : '-'}
            </DueDateText>
          ),
          sortComparator: (v1: number, v2: number) => {
            const current = dayjs().valueOf();
            return ((v1 ?? Infinity) - current) - ((v2 ?? Infinity) - current);
          },
        }]),
        {
          field: 'studentName',
          headerName: 'Student',
          width: 140,
        },
        {
          field: 'examLabel',
          headerName: 'Exam',
          width: 240,
        },
      ]}
      getRowId={(e) => `${e.student}#${e.examId}#${e.attemptNumber}`}
      {...props}
    />
  );
};

export default AttemptsList;
